/* Purchase options — drives variant and selling plan selection from the
   purchase-option cards rendered by snippets/purchase-options.liquid.

   Templates using this component may have the variant_picker block disabled, so
   the selected card is the single source of truth for both the variant id and
   the selling plan id submitted with the product form. */

if (!customElements.get('purchase-options')) {
  customElements.define(
    'purchase-options',
    class PurchaseOptions extends HTMLElement {
      connectedCallback() {
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.inputs = Array.from(this.querySelectorAll('.purchase-option__input'));
        if (this.inputs.length === 0) return;

        this.sellingPlanInput = this.querySelector('[data-purchase-options-selling-plan]');
        this.form = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
        this.productInfo = this.closest('product-info');

        this.addEventListener('change', this.onChange, { signal: this.abortController.signal });

        // Nothing checked server-side (for example the previously selected
        // variant is sold out) — fall back to the first available option.
        if (!this.inputs.some((input) => input.checked)) {
          this.inputs[0].checked = true;
        }

        this.apply(this.checkedInput);
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      get checkedInput() {
        return this.inputs.find((input) => input.checked) || null;
      }

      onChange = (event) => {
        if (!event.target.classList.contains('purchase-option__input')) return;
        this.apply(event.target);
      };

      apply(input) {
        if (!input) return;

        this.syncSelectedState(input);
        this.syncSellingPlan(input);
        const variant = this.variantFromInput(input);
        if (!variant) return;

        this.syncVariant(variant);
        this.syncProductUi(variant);
        this.syncUrl(variant.id, input.dataset.sellingPlanId || '');
        this.publishVariantChange(variant);
      }

      /* Class fallback so the selected state also works without :has() */
      syncSelectedState(input) {
        this.inputs.forEach((option) => {
          const card = option.closest('.purchase-option');
          if (card) card.classList.toggle('purchase-option--selected', option === input);
        });
      }

      syncSellingPlan(input) {
        if (!this.sellingPlanInput) return;

        const planId = input.dataset.sellingPlanId || '';
        if (this.sellingPlanInput.value === planId) return;

        this.sellingPlanInput.value = planId;
        // An empty selling_plan must not be submitted as a one-time purchase
        // key, so the input is disabled rather than sent blank.
        this.sellingPlanInput.disabled = planId === '';
        this.sellingPlanInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      syncVariant(variant) {
        const variantInput = this.variantInput();
        if (!variantInput) return;

        const variantId = String(variant.id);
        const changed = variantInput.value !== variantId;
        variantInput.value = variantId;
        variantInput.disabled = false;
        if (changed) variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      variantFromInput(input) {
        const variantId = input.dataset.variantId;
        if (!variantId) return null;

        const mediaId = input.dataset.featuredMediaId;
        return {
          id: Number(variantId),
          available: input.dataset.available !== 'false',
          featured_media: mediaId ? { id: Number(mediaId) } : null,
        };
      }

      syncProductUi(variant) {
        const pickupAvailability = this.productInfo?.querySelector('pickup-availability');
        if (pickupAvailability) {
          pickupAvailability.dataset.variantId = String(variant.id);
          pickupAvailability.update?.(variant);
        }

        const mediaId = variant.featured_media?.id;
        const desktopGallery = this.productInfo?.querySelector('media-gallery');
        if (mediaId && desktopGallery?.setActiveMedia) {
          desktopGallery.setActiveMedia(`${this.dataset.sectionId}-${mediaId}`, true);
        }

        const submitButton = this.productInfo?.querySelector('[id^="ProductSubmitButton-"]');
        if (submitButton) submitButton.disabled = !variant.available;
      }

      syncUrl(variantId, sellingPlanId) {
        if (this.productInfo?.dataset.updateUrl === 'false') return;

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        if (sellingPlanId) {
          url.searchParams.set('selling_plan', sellingPlanId);
        } else {
          url.searchParams.delete('selling_plan');
        }
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }

      publishVariantChange(variant) {
        if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.dataset.sectionId,
            variant,
          },
        });
      }

      variantInput() {
        const scope = this.form || document;
        return (
          scope.querySelector('input[name="id"].product-variant-id') ||
          scope.querySelector('input[name="id"]')
        );
      }
    }
  );
}
